import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  HStack,
  useColorModeValue,
  Button,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  useDisclosure,
  Input,
  FormControl,
  Select,
  Divider
} from '@chakra-ui/react';
import { 
  CheckIcon, 
  PhoneIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  SettingsIcon,
  WarningIcon,
  InfoIcon
} from '@chakra-ui/icons';
import { ModalAlert } from '@/components/ModalStyled/Modal';
import { BoxTitle } from '@/components/BoxTitle';
import { IoIosSend } from 'react-icons/io';
import { AiOutlineAudio, AiOutlineLike } from 'react-icons/ai';
import { BsTruck } from 'react-icons/bs';
import { TbLayoutKanban } from 'react-icons/tb';

interface StepProps {
  id: string;
  icon: React.ReactElement;
  title: string;
  isActive: boolean;
  bgColor: string;
  onClick: () => void;
}

const Step: React.FC<StepProps> = ({ id, icon, title, isActive, bgColor, onClick }) => {
  return (
    <Box
      position="relative"
      cursor="pointer"
      onClick={onClick}
      mx={4}
    >
      <Flex
        align="center"
        bg={'rgba(15, 79, 109, 1)'}
        borderRadius="xl"
        px={4}
        pl={1}
        py={2}
        transition="all 0.3s"
        _hover={{ shadow: 'md' }}
        minW="180px"
      >
        <Box
          ml={'-4'}
          my={'-2'}
          bg={bgColor}
          color="white"
          p={2}
          borderLeftRadius={'xl'}
          mr={3}
        >
          {icon}
        </Box>
        <Text
          fontWeight="medium"
          fontSize="sm"
          color={'white'}
        >
          {title}
        </Text>
      </Flex>
    </Box>
  );
};

interface FooterActionProps {
  tableId?: string;
}

const FooterAction: React.FC<FooterActionProps> = ({ tableId }) => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<string | null>(null);
  const [showReturnModal, setShowReturnModal] = useState<{ type: string; success: boolean } | null>(null);
  const [docaNumber, setDocaNumber] = useState<string>('');
  const [selectedMovementType, setSelectedMovementType] = useState<string>('');
  const [confirmationStep, setConfirmationStep] = useState<number>(0); // 0: seleção, 1: confirmação, 2: sucesso
  const { isOpen: isPopoverOpen, onOpen: onPopoverOpen, onClose: onPopoverClose } = useDisclosure();
  
  const steps = [
    {
      id: "chegada-confirmada",
      icon: <IoIosSend size={'24px'} />,
      title: "Chegada Confirmada",
      bgColor: "green.500"
    },
    {
      id: "chamar-motorista",
      icon: <AiOutlineAudio size={'24px'} />,
      title: "Chamar Motorista",
      bgColor: "orange.500"
    },
    {
      id: "confirmar-entrada",
      icon: <AiOutlineLike size={'24px'} />,
      title: "Confirmar Entrada",
      bgColor: "blue.500"
    },
    {
      id: "saida",
      icon: <BsTruck size={'24px'} />,
      title: "Saída",
      bgColor: "red.500"
    },
    {
      id: "operacoes",
      icon: <TbLayoutKanban size={'24px'} />,
      title: "Operações",
      bgColor: "purple.600"
    }
  ];


  const handleStepClick = (index: number, stepId: string) => {
    setActiveStep(index);
    
    if (stepId === "operacoes") {
      onPopoverOpen();
    } else if (stepId === "chegada-confirmada") {
      setConfirmationStep(0);
      setSelectedMovementType('');
      setOpenModal(stepId);
    } else {
      setOpenModal(stepId);
    }
  };

  const closeModal = () => {
    setOpenModal(null);
    setSelectedMovementType('');
    setConfirmationStep(0);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(null);
    setDocaNumber('');
  };

  const closeReturnModal = () => {
    setShowReturnModal(null);
  };

  // Fluxo de confirmação de chegada em 3 etapas
  const handleChegadaConfirmadaStep1 = () => {
    if (selectedMovementType) {
      setConfirmationStep(1);
    }
  };

  const handleChegadaConfirmadaStep2 = () => {
    // Simular requisição
    const success = Math.random() > 0.1; // 90% de chance de sucesso
    if (success) {
      setConfirmationStep(2);
    } else {
      setShowReturnModal({ type: 'chegada', success: false });
      closeModal();
    }
  };

  const handleChegadaConfirmadaStep3 = () => {
    closeModal();
  };

  const handleChegadaConfirmadaCancel = () => {
    if (confirmationStep === 1) {
      setConfirmationStep(0);
    } else {
      closeModal();
    }
  };

  const handleChamarMotorista = () => {
    setShowConfirmModal('chamar-motorista');
    closeModal();
  };

  const handleConfirmChamarMotorista = () => {
    // Simular requisição
    const success = Math.random() > 0.3;
    setShowReturnModal({ type: 'chamar-motorista', success });
    closeConfirmModal();
  };

  const handleConfirmDoca = () => {
    if (docaNumber.trim()) {
      // Simular requisição
      const success = Math.random() > 0.3;
      setShowReturnModal({ type: 'doca', success });
      closeConfirmModal();
    }
  };

  const handleConfirmarEntrada = () => {
    // Simular requisição
    const success = Math.random() > 0.3;
    setShowReturnModal({ type: 'entrada', success });
    closeModal();
  };

  const handleConfirmarSaida = () => {
    // Simular requisição
    const success = Math.random() > 0.3;
    setShowReturnModal({ type: 'saida', success });
    closeModal();
  };

  const renderModalContent = () => {
    switch (openModal) {
      case "chegada-confirmada":
        if (confirmationStep === 0) {
          // Etapa 1: Seleção do tipo de movimento
           return {
          title: <Text>Tipo de Movimento</Text>,
          body: (
            <VStack pt={3} spacing={4} align="stretch">
              <FormControl>
                <BoxTitle title='Tipo de Movimento'>
                  <Select 
                    placeholder="Selecione o tipo de movimento"
                    value={selectedMovementType}
                    onChange={(e) => setSelectedMovementType(e.target.value)}
                  >
                    <option value="entrada">Entrada</option>
                    <option value="saida">Saída</option>
                    <option value="transferencia">Transferência</option>
                    <option value="devolucao">Devolução</option>
                  </Select>
                </BoxTitle>
              </FormControl>
            </VStack>
          ),
          footer: (
            <HStack p={0}m={0} >
              <Button size={'sm'} variant="outline" onClick={closeModal}>
                Cancelar
              </Button>
              <Button 
                size={'sm'}
                colorScheme="green" 
                onClick={handleChegadaConfirmadaStep1}
                isDisabled={!selectedMovementType}
              >
                OK
              </Button>
            </HStack>
          )
        };
        } else if (confirmationStep === 1) {
          // Etapa 2: Confirmação
          return {
            title: (
              <HStack>
                <WarningIcon color="orange.500" />
                <Text>AVISO</Text>
              </HStack>
            ),
            body: (
              <VStack pt={3} spacing={4} align="center">
                <Text fontSize="md" textAlign="center">
                  Confirmar chegada?
                </Text>
              </VStack>
            ),
            footer: (
              <HStack spacing={3}>
                <Button 
                  size="sm"
                  colorScheme="green" 
                  onClick={handleChegadaConfirmadaStep2}
                  minW="80px"
                >
                  OK
                </Button>
                <Button 
                  size="sm"
                  colorScheme="red" 
                  onClick={handleChegadaConfirmadaCancel}
                  minW="80px"
                >
                  Cancelar
                </Button>
              </HStack>
            )
          };
        } else if (confirmationStep === 2) {
          // Etapa 3: Sucesso
           return {
            title: (
              <HStack>
                <WarningIcon color="orange.500" />
                <Text>AVISO</Text>
              </HStack>
            ),
            body: (
              <VStack pt={3} spacing={4} align="center">
                <Text fontSize="md" textAlign="center">
                  Operação realizada com sucesso!
                </Text>
              </VStack>
            ),
            footer: (
              <HStack justify="center">
                <Button 
                  size="sm"
                  colorScheme="green" 
                  onClick={handleChegadaConfirmadaStep3}
                  minW="80px"
                >
                  OK
                </Button>
              </HStack>
            )
          };
        }
        break;

      case "chamar-motorista":
        return {
          title: <Text>Chamar Motorista</Text>,
          body: (
            <VStack pt={3} alignItems={'center'} spacing={4} w={'100%'} justify={'center'} align="stretch">
              <Text>Confirma liberação da doca?</Text>
            </VStack>
          ),
          footer: (
            <HStack>
              <Button size={'sm'} variant="outline" onClick={closeModal}>
                Cancelar
              </Button>
              <Button size={'sm'} colorScheme="orange" onClick={handleChamarMotorista}>
                OK
              </Button>
            </HStack>
          )
        };

      case "confirmar-entrada":
        return {
          title: <Text>Confirmar Entrada</Text>,
          body: (
            <VStack pt={3} alignItems={'center'} spacing={4} w={'100%'} justify={'center'} align="stretch">
              <Text>Confirma entrada da Carga?</Text>
            </VStack>
          ),
          footer: (
            <HStack>
              <Button size={'sm'} variant="outline" onClick={closeModal}>
                Cancelar
              </Button>
              <Button size={'sm'} colorScheme="blue" onClick={handleConfirmarEntrada}>
                OK
              </Button>
            </HStack>
          )
        };

      case "saida":
        return {
          title: <Text>Confirmar Saída</Text>,
          body: (
            <VStack pt={3} alignItems={'center'} spacing={4} w={'100%'} justify={'center'} align="stretch">
              <Text>Confirma saída final da Carga?</Text>
            </VStack>
          ),
          footer: (
            <HStack>
              <Button size={'sm'} variant="outline" onClick={closeModal}>
                Cancelar
              </Button>
              <Button size={'sm'} colorScheme="red" onClick={handleConfirmarSaida}>
                OK
              </Button>
            </HStack>
          )
        };

      default:
        return {
          title: "Modal",
          body: <Text>Conteúdo do modal</Text>,
          footer: <Button onClick={closeModal}>Fechar</Button>
        };
    }
  };

  const renderConfirmModal = () => {
    switch (showConfirmModal) {
      case "chamar-motorista":
        return {
          title: <Text>Doca</Text>,
          body: (
            <VStack pt={3} spacing={4} align="stretch">
              <FormControl>
                <BoxTitle title='Doca'>
                  <Input 
                    placeholder="Digite o número da doca"
                    value={docaNumber}
                    onChange={(e) => setDocaNumber(e.target.value)}
                  />
                </BoxTitle>
              </FormControl>
            </VStack>
          ),
          footer: (
            <HStack>
              <Button  size={'sm'} variant="outline" onClick={closeConfirmModal}>
                Cancelar
              </Button>
              <Button
                size={'sm'} 
                colorScheme="blue" 
                onClick={handleConfirmDoca}
                isDisabled={!docaNumber.trim()}
              >
                OK
              </Button>
            </HStack>
          )
        };

      default:
        return null;
    }
  };

  const renderReturnModal = () => {
    if (!showReturnModal) return null;

    const { type, success } = showReturnModal;
    
    let title = "AVISO";
    let message = "";

    if (success) {
      message = "Operação realizada com sucesso!";
    } else {
      switch (type) {
        case "chegada":
          message = "Erro ao registrar tipo de movimento.";
          break;
        case "chamar-motorista":
          message = "Erro ao chamar motorista.";
          break;
        case "doca":
          message = "Erro ao liberar doca.";
          break;
        case "entrada":
          message = "Erro ao confirmar entrada.";
          break;
        case "saida":
          message = "Erro ao confirmar saída.";
          break;
      }
    }

    return {
      title: (
        <HStack>
          <WarningIcon color="orange.500" />
          <Text>{title}</Text>
        </HStack>
      ),
      body: (
        <VStack pt={3} spacing={4} align="center">
          <Text fontSize="md" textAlign="center" color={success ? "green.500" : "red.500"}>
            {message}
          </Text>
        </VStack>
      ),
      footer: (
        <HStack justify="center">
          <Button 
            size="sm"
            colorScheme="green" 
            onClick={closeReturnModal}
            minW="80px"
          >
            OK
          </Button>
        </HStack>
      )
    };
  };

  const modalContent = renderModalContent();
  const confirmModalContent = renderConfirmModal();
  const returnModalContent = renderReturnModal();

  return (
    <Box mt={2} mx="auto">
      <VStack spacing={2}>
        <Flex
          direction="row"
          align="center"
          justify="center"
          wrap="wrap"
          gap={0}
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              {step.id === "operacoes" ? (
                <Popover isOpen={isPopoverOpen} onClose={onPopoverClose}>
                  <PopoverTrigger>
                    <Box>
                      <Step
                        id={step.id}
                        icon={step.icon}
                        title={step.title}
                        isActive={activeStep === index}
                        bgColor={step.bgColor}
                        onClick={() => handleStepClick(index, step.id)}
                      />
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverHeader>
                      <HStack>
                        <SettingsIcon color="purple.600" />
                        <Text>Operações Disponíveis</Text>
                      </HStack>
                    </PopoverHeader>
                    <PopoverBody>
                      <VStack spacing={3} align="stretch">
                        <Button size="sm" colorScheme="blue" variant="outline">
                          Relatório de Carga
                        </Button>
                        <Button size="sm" colorScheme="green" variant="outline">
                          Verificar Documentos
                        </Button>
                        <Button size="sm" colorScheme="orange" variant="outline">
                          Agendar Manutenção
                        </Button>
                        <Button size="sm" colorScheme="red" variant="outline">
                          Reportar Problema
                        </Button>
                        <Divider />
                        <Text fontSize="xs" color="gray.500">
                          Selecione uma operação para continuar
                        </Text>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : (
                <Step
                  id={step.id}
                  icon={step.icon}
                  title={step.title}
                  isActive={activeStep === index}
                  bgColor={step.bgColor}
                  onClick={() => handleStepClick(index, step.id)}
                />
              )}
            </React.Fragment>
          ))}
        </Flex>

        {openModal && modalContent && (
          <ModalAlert
            isOpen={true}
            size="sm"
            title={modalContent.title}
            body={modalContent.body}
            footer={modalContent.footer}
            onClose={closeModal}
          />
        )}

        {showConfirmModal && confirmModalContent && (
          <ModalAlert
            isOpen={true}
            size="sm"
            title={confirmModalContent.title}
            body={confirmModalContent.body}
            footer={confirmModalContent.footer}
            onClose={closeConfirmModal}
          />
        )}

        {showReturnModal && returnModalContent && (
          <ModalAlert
            isOpen={true}
            size="sm"
            title={returnModalContent.title}
            body={returnModalContent.body}
            footer={returnModalContent.footer}
            onClose={closeReturnModal}
          />
        )}
      </VStack>
    </Box>
  );
};

export default FooterAction;